import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring, useScroll, animate } from "framer-motion";

// Smooth momentum scrolling via Lenis
export function useLenis() {
  useEffect(() => {
    let lenis;
    let raf;
    (async () => {
      const Lenis = (await import("lenis")).default;
      lenis = new Lenis({ duration: 1.15, smoothWheel: true, lerp: 0.09 });
      const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
      window.__lenis = lenis;
    })();
    return () => { cancelAnimationFrame(raf); if (lenis) lenis.destroy(); };
  }, []);
}

export function scrollToId(href) {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
}

// Scroll-reveal wrapper
export function Reveal({ children, delay = 0, y = 40, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// Masked line-by-line reveal for hero
export function MaskLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

// Animated counter — animates on mount (used for above-the-fold hero stats)
export function Counter({ to, suffix = "", duration = 1.8 }) {
  const [val, setVal] = useState(0);
  const mv = useMotionValue(0);
  useEffect(() => {
    const controls = animate(mv, to, {
      duration, ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return controls.stop;
  }, [to, duration, mv]);
  return <span>{val}{suffix}</span>;
}

export function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`relative py-24 md:py-32 px-6 ${className}`}>
      <div className="mx-auto max-w-7xl">{children}</div>
    </section>
  );
}

export function Overline({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-primary">
      <span className="h-px w-8 bg-primary/60" />
      {children}
    </span>
  );
}

// Top scroll progress bar (brand-gradient)
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.2 });
  return (
    <motion.div
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-[3px] bg-brand-gradient origin-left z-[60] pointer-events-none"
    />
  );
}

// Soft radial cursor glow (desktop only)
export function CursorGlow() {
  const x = useMotionValue(-300);
  const y = useMotionValue(-300);
  const sx = useSpring(x, { stiffness: 220, damping: 30, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 30, mass: 0.4 });
  useEffect(() => {
    const move = (e) => { x.set(e.clientX); y.set(e.clientY); };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [x, y]);
  return (
    <motion.div
      style={{ left: sx, top: sy, translateX: "-50%", translateY: "-50%" }}
      className="pointer-events-none fixed z-[45] hidden md:block h-[28rem] w-[28rem] rounded-full"
      aria-hidden
    >
      <div
        className="h-full w-full rounded-full opacity-70"
        style={{ background: "radial-gradient(circle, rgba(255,31,142,0.12), rgba(249,115,22,0.06) 40%, transparent 65%)" }}
      />
    </motion.div>
  );
}

// Magnetic attraction wrapper — pulls its child toward the cursor
export function Magnetic({ children, strength = 0.28, className = "" }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 240, damping: 18 });
  const sy = useSpring(y, { stiffness: 240, damping: 18 });
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => { x.set(0); y.set(0); };
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
}

// Animated underline nav link
export function NavLink({ children, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group relative px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
    >
      {children}
      <span className="absolute left-4 right-4 -bottom-0.5 h-[2px] scale-x-0 bg-brand-gradient origin-left transition-transform duration-300 group-hover:scale-x-100 rounded-full" />
    </button>
  );
}

